/**
 * AppointmentCancelView UIコンポーネント。frontend-components.md準拠。US-D2-11、BR-U6-11。
 *
 * <p>2026-08-19デザイン更新: 予約確定後の表示（旧AppointmentConfirmationView）は、
 * PreBookingForm/MultiCvAppointmentList/SurveyPopup（bookingSectionUi経由）が
 * カード内を完了メッセージへ差し替える方式に統合されたため、本ファイルからは削除した
 * （ICS/Googleカレンダー追加のロジックは`core-ui/calendarAddActionsUi.ts`へ移設済み）。
 * AppointmentCancelView（トップページ常時埋め込み、cancel_appointment_idパラメータ検知時のみ
 * 起動する独立モーダル）は本デザイン更新のスコープ外のため変更していない。
 *
 * design-mockups/appointment-confirmation-and-cancel.html準拠:
 * - AppointmentCancelView: キャンセル確認・キャンセル期限超過メッセージを強化。
 *   2026-07-24申し送り: キャンセル確認前の企業名・日時の事前表示は、spica-api側に
 *   アポイント単体取得用GETエンドポイントが存在しないため見送り（aidlc-state.md記録済み）。
 *
 * <p><b>2026-08-25バグ修正</b>: 未ログイン時の表示を、他ウィジェット（SurveyPopup/PreBookingForm/
 * MultiCvAppointmentList）と統一されたWidgetPanelのログインゲート方式（モーダル内コンテンツを
 * ブラーし、同一モーダル内にログインカードを重畳する）に変更した。従来は中央モーダル（キャンセル
 * フォームの下地）と右下の別ポップアップ（LoginPromptOverlay、汎用文言）を同時に表示しており、
 * 二重表示・文脈に合わない文言（「ご回答内容に合わせて、最適なURLや...」）という不具合があった。
 */

import { createElement, createShadowHost, applyStylesheet } from "../core/shadowDomUtil";
import { cancelAppointmentAsVisitor } from "./cancelAppointmentAsVisitor";
import { relayClient } from "../core/relayClient";
import { setLoggedIn } from "../core/authState";
import { createWidgetPanel, buildWidgetLoginUrl, WIDGET_PANEL_STYLES } from "../core-ui/widgetPanelUi";
import type { LoginGateOptions } from "../core-ui/widgetPanelUi";
import { LOADING_SPINNER_STYLES, renderLoadingSpinner, setButtonLoading } from "../core-ui/loadingSpinnerUi";
import { DESIGN_TOKEN_CSS } from "../core-ui/designTokens";
import { formatCancelDateTime, nowJstDateTime, subtractHoursJst } from "../core-ui/datetime";
import type { AppointmentDetailResponse } from "../types";

const CONFIRMATION_STYLES = `
:host { all: initial; ${DESIGN_TOKEN_CSS} }
${WIDGET_PANEL_STYLES}
${LOADING_SPINNER_STYLES}
.spica-confirm-icon { font-size: 40px; text-align: center; margin-bottom: 8px; }
.spica-confirm-title { font-size: 16px; font-weight: bold; text-align: center; margin: 0 0 16px; color: #1a1a1a; }
.spica-confirm-detail { background: #f5f9ff; border-radius: 8px; padding: 12px; font-size: 13px; line-height: 1.6; margin: 0 0 16px 0; }
.spica-confirm-detail dt { color: #888; font-size: 11px; margin: 0; }
.spica-confirm-detail dd { margin: 0 0 6px 0; font-weight: bold; color: #1a1a1a; }
.spica-cancel-title { font-size: 15px; font-weight: bold; margin: 0 0 14px; color: #1a1a1a; }
.spica-cancel-reason-textarea { width: 100%; box-sizing: border-box; border: 1px solid #d0d5dd; border-radius: 8px; padding: 10px; font-size: 13px; resize: vertical; min-height: 60px; margin: 8px 0 14px; }
.spica-cancel-submit-button { width: 100%; padding: 12px; border: none; border-radius: 8px; background: #e53e3e; color: #fff; cursor: pointer; font-size: 14px; font-weight: bold; }
.spica-cancel-back-button { width: 100%; margin-top: 8px; padding: 10px; border: 1px solid #ccc; border-radius: 8px; background: #fff; color: #555; cursor: pointer; font-size: 13px; }
.spica-deadline-exceeded-box { background: #fff8e6; border: 1px solid #f6c94c; color: #6b5300; border-radius: 8px; padding: 12px; font-size: 13px; line-height: 1.6; margin-top: 12px; }
`;

/**
 * AppointmentCancelView: BR-U6-11準拠。cancel_appointment_idパラメータ検知時のみレンダリングする。
 *
 * 2026-08-26改修: アポイント詳細取得API（GET /api/public/appointments/{id}）を利用し、
 * 企業名・日時を動的表示。キャンセル期限（商談開始24時間前）を事前チェックし、
 * 期限超過時はキャンセルボタンを非表示化してメッセージを表示する。
 */
export function renderAppointmentCancelModal(appointmentId: string): void {
  const { host, shadowRoot } = createShadowHost("spica-appointment-cancel-modal");
  applyStylesheet(shadowRoot, CONFIRMATION_STYLES);

  const panel = createWidgetPanel({
    position: "center-modal",
    lead: "商談の確認・キャンセル",
    ariaLabel: "商談の確認・キャンセル",
    onClose: () => host.remove(),
  });
  shadowRoot.appendChild(panel.root);
  document.body.appendChild(host);

  // ローディング表示（スピナー）
  const loadingEl = renderLoadingSpinner({ text: "商談情報を取得しています..." });
  panel.content.appendChild(loadingEl);

  // キャンセル専用ログインゲートのオプション（API応答後に未ログインと判明した場合のみ表示する）
  const cancelLoginGateOptions: LoginGateOptions = {
    message: "商談をキャンセルするには\nログインが必要です",
    buttonLabel: "ログインする",
  };

  // アポイント詳細を非同期で取得
  void (async () => {
    const response = await relayClient.getAppointmentDetail<AppointmentDetailResponse>({ appointmentId });

    // ログイン状態を反映
    if (response.loggedIn !== null) {
      setLoggedIn(response.loggedIn);
    }

    // 未ログイン（401）の場合はゲートを表示
    // 401 時点では response.data が無い（アポ詳細未取得）ため companyRootId は渡せない
    // （2026-09-29 レビュー対応: このケースはアポ詳細自体が未取得のため元々渡せる情報がない。
    // 送信直前〔renderCancelForm 内〕の requiresLogin 分岐は detail 取得済みのため渡す）。
    if (response.status === 401) {
      panel.content.textContent = "";
      renderCancelFormPlaceholder(panel.content);
      panel.showLoginGate(buildWidgetLoginUrl("APPOINTMENT_CANCEL"), () => host.remove(), cancelLoginGateOptions);
      return;
    }

    // ネットワークエラー時のフォールバック
    if (response.networkError) {
      panel.content.textContent = "";
      const errorMsg = createElement("p", { className: "spica-cancel-title", text: "通信エラーが発生しました" });
      panel.content.appendChild(errorMsg);
      const retryButton = createElement("button", { className: "spica-cancel-back-button", text: "再試行" });
      retryButton.addEventListener("click", () => {
        host.remove();
        renderAppointmentCancelModal(appointmentId);
      });
      panel.content.appendChild(retryButton);
      const closeButton = createElement("button", { className: "spica-cancel-back-button", text: "閉じる" });
      closeButton.addEventListener("click", () => host.remove());
      panel.content.appendChild(closeButton);
      return;
    }

    panel.content.textContent = "";

    if (!response.ok || !response.data) {
      // アポイントが見つからない場合
      const errorMsg = createElement("p", { className: "spica-cancel-title", text: "商談情報を取得できませんでした" });
      panel.content.appendChild(errorMsg);
      const backButton = createElement("button", { className: "spica-cancel-back-button", text: "閉じる" });
      backButton.addEventListener("click", () => host.remove());
      panel.content.appendChild(backButton);
      return;
    }

    const detail = response.data;
    const companyName = detail.exhibitorCompanyName ?? "出展企業";
    // 期限超過ビュー・キャンセル済みビューの日時表示は基準日時（確定日時 or 希望日時最小値）を用いる。
    const referenceAt = resolveReferenceDateTime(detail);

    // キャンセル済みアポイントの場合はキャンセルフォームを表示せず、済み表示を出す
    if (detail.status === "CANCELLED") {
      renderAlreadyCancelledView(panel.content, companyName, referenceAt, host);
      return;
    }

    // キャンセル期限チェック（サーバーと同一基準: expired優先＋基準日時24時間前）
    const deadlineExceeded = isDeadlineExceeded(detail);

    if (deadlineExceeded) {
      renderDeadlineExceededView(panel.content, companyName, detail, host);
    } else {
      renderCancelForm(panel.content, appointmentId, host, panel, cancelLoginGateOptions, companyName, detail);
    }
  })();
}

/**
 * 基準日時を算出する（サーバー `Appointment.resolveReferenceDateTime` と同一ロジックのフロント再現）。
 * `scheduledStartAt`（確定日時）があればその値、なければ `desiredDateTime1〜3` のうち最小値（希望日時の
 * 一番早い候補）を返す。いずれもnullの場合はnullを返す。
 * JST壁時計文字列（`yyyy-MM-ddTHH:mm:ss`）は辞書順比較が時系列順と一致するため、文字列比較で最小値を求める
 * （`new Date(iso)`/epoch不使用、TZ非依存を維持。datetime-contract.md）。
 */
function resolveReferenceDateTime(detail: AppointmentDetailResponse): string | null {
  if (detail.scheduledStartAt) return detail.scheduledStartAt;
  let min: string | null = null;
  for (const candidate of [detail.desiredDateTime1, detail.desiredDateTime2, detail.desiredDateTime3]) {
    if (candidate && (min === null || candidate < min)) {
      min = candidate;
    }
  }
  return min;
}

/**
 * 未確定アポ（日時未確定のリクエスト・審査中）か判定する。
 * サーバーの `expired`（`isDisplayedAsExpired`）のステータスガードと整合させ、
 * `scheduledStartAt` が未設定かつ `status ∈ {REQUESTED, PENDING_REVIEW}` を未確定として扱う。
 */
function isUnconfirmed(detail: AppointmentDetailResponse): boolean {
  return !detail.scheduledStartAt && (detail.status === "REQUESTED" || detail.status === "PENDING_REVIEW");
}

/**
 * キャンセル期限を超過しているか判定する（サーバー `resolveReferenceDateTime` /
 * `isDisplayedAsExpired` と同一基準のフロント再現、BR-U2-5/BR-U2-19）。
 *
 * <p>(a) サーバー時刻基準の `expired` フラグ（未確定アポ限定）を優先的に期限超過として扱う。
 * (b) 基準日時（確定日時 or 希望日時最小値）の24時間前を過ぎているかを端末JST壁時計基準で判定する。
 * 基準日時が算出できない場合は期限判定対象外（false、サーバー `isCancellable` と同じ扱い）。
 *
 * <p>`desiredDateTime`/`expired` が欠落（中継が古い等）した場合は、`scheduledStartAt` のみの
 * 従来判定へフォールバックする（機能停止防止。押下時に `cancelAppointmentAsVisitor` が最終判定）。
 */
function isDeadlineExceeded(detail: AppointmentDetailResponse): boolean {
  // (a) サーバー時刻基準の expired フラグを優先する。
  if (detail.expired === true) return true;
  // (b) 基準日時の24時間前（端末JST壁時計基準）。
  // JST壁時計文字列同士の比較で判定する（`new Date(iso)`/epoch不使用、TZ非依存を維持）。
  // 期限ちょうど（基準日時の24時間前と同時刻）も超過扱いとし、サーバー
  // `Appointment.isPastCancellationDeadline`（`!now.isBefore(deadline)` ＝ `now >= deadline`）と
  // 判定基準を一致させる（`>` ではなく `>=`）。
  const referenceAt = resolveReferenceDateTime(detail);
  if (!referenceAt) return false;
  const deadlineStr = subtractHoursJst(referenceAt, 24);
  if (!deadlineStr) return false;
  return nowJstDateTime() >= deadlineStr;
}

/** 日時をユーザー向けにフォーマット */
function formatDateTime(isoString: string | null): string {
  // JSTローカル文字列を文字列操作で整形する（`new Date(iso)` 不使用、TZ非依存。datetime-contract.md §4）。
  return formatCancelDateTime(isoString);
}

/** 既にキャンセル済みのアポイントに対する表示 */
function renderAlreadyCancelledView(container: HTMLElement, companyName: string, scheduledAt: string | null, host: HTMLElement): void {
  const icon = createElement("div", { className: "spica-confirm-icon", text: "ℹ️" });
  container.appendChild(icon);
  const title = createElement("p", { className: "spica-confirm-title", text: "この商談は既にキャンセル済みです" });
  container.appendChild(title);

  container.appendChild(buildAppointmentDetailDl(companyName, scheduledAt));

  const backButton = createElement("button", { className: "spica-cancel-back-button", text: "閉じる" });
  backButton.addEventListener("click", () => host.remove());
  container.appendChild(backButton);
}

/** キャンセル期限超過時の表示（確定/未確定で文言・日時ラベルを出し分ける） */
function renderDeadlineExceededView(container: HTMLElement, companyName: string, detail: AppointmentDetailResponse, host: HTMLElement): void {
  const unconfirmed = isUnconfirmed(detail);
  const referenceAt = resolveReferenceDateTime(detail);

  const title = createElement("p", { className: "spica-cancel-title", text: "商談をキャンセルしますか？" });
  container.appendChild(title);

  container.appendChild(buildAppointmentDetailDl(companyName, referenceAt, unconfirmed));

  const errorBox = createElement("div", {
    className: "spica-deadline-exceeded-box",
    text: unconfirmed
      ? "ご希望日時の24時間前を過ぎたため、このリクエストは無効となりました。あらためてお申し込みください。"
      : "キャンセル期限（商談開始24時間前）を過ぎているため、こちらからのキャンセルはできません。出展企業へお問い合わせください。",
  });
  container.appendChild(errorBox);

  const backButton = createElement("button", { className: "spica-cancel-back-button", text: "閉じる" });
  backButton.addEventListener("click", () => host.remove());
  container.appendChild(backButton);
}

/** 未ログイン時のプレースホルダー表示（ゲートの下に見える部分） */
function renderCancelFormPlaceholder(container: HTMLElement): void {
  const title = createElement("p", { className: "spica-cancel-title", text: "商談をキャンセルしますか？" });
  container.appendChild(title);

  const detail = createElement("dl", { className: "spica-confirm-detail" });
  detail.appendChild(createElement("dt", { text: "企業" }));
  detail.appendChild(createElement("dd", { text: "出展企業" }));
  detail.appendChild(createElement("dt", { text: "日時" }));
  detail.appendChild(createElement("dd", { text: "—" }));
  container.appendChild(detail);
}

function renderCancelForm(container: HTMLElement, appointmentId: string, host: HTMLElement, panel: { showLoginGate: (loginUrl: string, onCancel?: () => void, options?: LoginGateOptions) => void; hideLoginGate: () => void }, cancelLoginGateOptions: LoginGateOptions, companyName: string, detail: AppointmentDetailResponse): void {
  const unconfirmed = isUnconfirmed(detail);
  const referenceAt = resolveReferenceDateTime(detail);

  const title = createElement("p", { className: "spica-cancel-title", text: "商談をキャンセルしますか？" });
  container.appendChild(title);

  container.appendChild(buildAppointmentDetailDl(companyName, referenceAt, unconfirmed));

  const reasonLabel = createElement("label", { text: "キャンセル理由（任意）" });
  container.appendChild(reasonLabel);

  const reasonInput = createElement("textarea", {
    className: "spica-cancel-reason-textarea",
    attrs: { placeholder: "都合により参加できなくなりました" },
  });
  container.appendChild(reasonInput);

  const submitButton = createElement("button", { className: "spica-cancel-submit-button", text: "キャンセルする" });
  submitButton.addEventListener("click", async () => {
    const restoreButton = setButtonLoading(submitButton as HTMLButtonElement, "キャンセル中...");
    // 効果分析ログ（イベントK、BR-AN-7）: companyRootId をアポ詳細から渡す（テナント境界確定）。
    // 本モーダルはトップページ常時埋め込みの cancel_appointment_id 起動のみのため cancelEntryPoint は
    // 固定で TOP_PAGE_MODAL（2026-09-29 レビュー反映）。
    const result = await cancelAppointmentAsVisitor(appointmentId, reasonInput.value || undefined, {
      companyRootId: detail.companyRootId,
      cancelEntryPoint: "TOP_PAGE_MODAL",
    });

    if (result.requiresLogin) {
      // セッション切れ等: ログインゲートを表示する
      // 効果分析ログ（イベントE、BR-AN-7）: detail.companyRootId を渡す（2026-09-29 レビュー対応、
      // テナント境界確定漏れの修正）。
      restoreButton();
      panel.showLoginGate(
        buildWidgetLoginUrl("APPOINTMENT_CANCEL", { companyRootId: detail.companyRootId }),
        () => host.remove(),
        cancelLoginGateOptions
      );
      return;
    }

    if (result.deadlineExceeded) {
      // Race condition対応: API取得〜キャンセル実行の間に期限を過ぎた場合のフォールバック
      container.textContent = "";
      renderDeadlineExceededView(container, companyName, detail, host);
      return;
    }

    if (result.success) {
      container.textContent = "";
      const doneIcon = createElement("div", { className: "spica-confirm-icon", text: "✅" });
      container.appendChild(doneIcon);
      const doneMsg = createElement("p", { className: "spica-confirm-title", text: "キャンセルが完了しました" });
      container.appendChild(doneMsg);
    }

    if (!result.success && !result.requiresLogin && !result.deadlineExceeded) {
      // キャンセル済み・不正な状態遷移等のビジネスエラー
      container.textContent = "";
      renderAlreadyCancelledView(container, companyName, referenceAt, host);
    }
  });
  container.appendChild(submitButton);

  const backButton = createElement("button", { className: "spica-cancel-back-button", text: "閉じる" });
  backButton.addEventListener("click", () => host.remove());
  container.appendChild(backButton);
}

/**
 * 企業名・日時の表示（<dl>）。アポイント詳細APIから取得した実データを表示する。
 * `unconfirmed=true`（日時未確定のリクエスト・審査中）の場合、日時ラベルを「ご希望日時」に切り替える
 * （確定日時と誤読されないようにする。他画面の表示系ラベル「希望日時」に整合、ユーザー向けに「ご」を付与）。
 */
function buildAppointmentDetailDl(companyName: string, dateTime: string | null, unconfirmed = false): HTMLElement {
  const detail = createElement("dl", { className: "spica-confirm-detail" });
  detail.appendChild(createElement("dt", { text: "企業" }));
  detail.appendChild(createElement("dd", { text: companyName }));
  detail.appendChild(createElement("dt", { text: unconfirmed ? "ご希望日時" : "日時" }));
  detail.appendChild(createElement("dd", { text: formatDateTime(dateTime) }));
  return detail;
}
